import { createLoadable } from 'dreamstate';

import { Maybe, Optional } from '@vimeo/core/types/generic';
import { Viewer } from '@vimeo/viewer';

import { queryManagerGctlConfigWithFallback } from '../../../api/base';
import {
  ECredentialsType,
  postInteractionRoomAuth,
  postRoomCredentialsCheck,
} from '../../../api/interaction';
import { interactionToolsConfig } from '../../../configs/InteractionToolsConfig';
import { localStorageConfig } from '../../../configs/LocalStorageConfig';
import { EComposerSessionType } from '../../../types/composer_session';
import { ELiveErrorCode } from '../../../types/error';
import {
  EInteractionAuthOption,
  EInteractionRoomPrivacy,
  IAuthorizeInteractionUserParams,
  IInteractionRoomCredentials,
  ISavedUserAuthPreferences,
} from '../../../types/interaction_room';
import { getIframeReferrer } from '../../../utils/embed/embed_referrer';
import { LiveError } from '../../../utils/error';
import { inline } from '../../../utils/general/lambda';
import { getFromLocalStorage, setLocalStorageItem } from '../../../utils/general/local_storage';
import { isEmailValid } from '../../../utils/general/validators';
import { queryCurrentViewerSync } from '../../../utils/store/viewer';
import { ELiveErrorCategory, trackLiveError } from '../../../utils/tracking';
import { ELiveSignal, TInteractionSessionReadySignalEvent } from '../../signals';
import type { InteractionSessionManager } from './InteractionSessionManager';

/**
 * Check auto auth status and skip login screen if needed.
 */
export async function checkAutoAuth(manager: InteractionSessionManager): Promise<void> {
  const {
    roomInfo: { value: roomInfo },
    roomPassword,
    roomHashedPassword,
    roomInjectedAuthToken,
    roomRegistrantUuid,
    roomDisabledAuthOptions,
  } = manager.context;

  manager.log.info(
    'Checking auto-login options:',
    manager.isAuthAutoSignInDisabled,
    roomInfo?.settings.authOptions,
    roomDisabledAuthOptions,
  );

  manager.assertIsInitialized();

  /**
   * Allow skipping auto-auth flow with parameters.
   */
  if (manager.isAuthAutoSignInDisabled) {
    return manager.log.info('Auto auth is not forced, continue with separate options flow');
  }

  const viewer: Viewer = queryCurrentViewerSync(manager);

  const hasNoRequiredPassword: boolean =
    roomInfo?.settings.privacy === EInteractionRoomPrivacy.PASSWORD &&
    !roomPassword &&
    !roomHashedPassword;
  const canHookUpAdminAuth: boolean = Boolean(
    roomInfo?.settings.authOptions[EInteractionAuthOption.ADMIN],
  );
  const canHookUpVimeoAuth: boolean = Boolean(
    roomInfo?.settings.authOptions[EInteractionAuthOption.VIMEO] && viewer.user,
  );
  const canHookUpRegistrantAuth: boolean = Boolean(
    roomInfo?.settings.authOptions[EInteractionAuthOption.REGISTRANT] && roomRegistrantUuid,
  );

  const savedUserAuthPreferences: Optional<ISavedUserAuthPreferences> =
    getFromLocalStorage<ISavedUserAuthPreferences>(
      localStorageConfig.INTERACTION.SAVED_USER_AUTH_PREFERENCES,
    );
  // time in seconds for matching saved format
  const accessRoomTime: number = Math.floor(Date.now() / 1000);
  const isSavedUserAuthPreferencesValid: boolean = Boolean(
    roomInfo?.roomType !== EComposerSessionType.VENUE &&
      savedUserAuthPreferences &&
      savedUserAuthPreferences.roomId === roomInfo?.roomId &&
      savedUserAuthPreferences.authToken.jwt &&
      savedUserAuthPreferences.authToken.exp >= accessRoomTime,
  );

  const canHookUpJwtAuth: boolean = Boolean(
    roomInfo?.settings.authOptions[EInteractionAuthOption.ANONYMOUS] &&
      !canHookUpVimeoAuth &&
      (roomInjectedAuthToken || isSavedUserAuthPreferencesValid),
  );

  if (canHookUpAdminAuth) {
    manager.log.info('Detected admin permissions, continue with auto-login flow');
  } else if (hasNoRequiredPassword) {
    return manager.log.info(
      'Password protected event with not provided password, auto auth is not available.',
    );
  }

  try {
    if (canHookUpAdminAuth) {
      manager.log.info('Processing auto-login with admin vimeo user');

      await authorizeInteractionUser(manager, { type: EInteractionAuthOption.ADMIN });
    } else if (canHookUpVimeoAuth) {
      manager.log.info('Processing auto-login with vimeo user');

      await authorizeInteractionUser(manager, { type: EInteractionAuthOption.VIMEO });
    } else if (canHookUpJwtAuth) {
      manager.log.info('Processing auto-login with anonymous user based on jwt');

      const userParams = inline(() => {
        if (roomInjectedAuthToken) {
          return {};
        } else {
          return {
            roomDisplayName: savedUserAuthPreferences?.displayName,
            roomSavedUserAuthToken: savedUserAuthPreferences?.authToken.jwt,
          };
        }
      });

      /**
       * In the future jwt can contain auth type inside, and the token will be the only parameter for login
       */
      await authorizeInteractionUser(manager, {
        type: EInteractionAuthOption.ANONYMOUS,
        ...userParams,
      });
    } else if (canHookUpRegistrantAuth) {
      manager.log.info('Processing auto-login with registrant user');

      await authorizeInteractionUser(manager, {
        type: EInteractionAuthOption.REGISTRANT,
        roomRegistrantUuid: roomRegistrantUuid as string,
      });
    } else {
      manager.log.info('No possible auto-login options, fallback to manual variant');
    }
  } catch (error) {
    manager.log.error('Auto-login flow failed:', error);
    trackLiveError(error, { method: 'checkAutoAuth', category: ELiveErrorCategory.INTERACTION });
  }
}

/**
 * Authorize in interaction tools session.
 */
export async function authorizeInteractionUser(
  manager: InteractionSessionManager,
  {
    type,
    roomDisplayName,
    roomRegistrantUuid,
    roomGuestCode,
    roomSavedUserAuthToken,
    roomEmail,
  }: IAuthorizeInteractionUserParams,
): Promise<void> {
  const { roomPassword, roomHashedPassword, roomUnlistedHash, roomInjectedAuthToken } =
    manager.context;
  const referrer: Optional<string> = getIframeReferrer();

  manager.log.info('Authorizing in room:', {
    type,
    roomDisplayName,
    roomGuestCode,
    roomPassword,
    roomHashedPassword,
    roomUnlistedHash,
    roomJwtToken: roomInjectedAuthToken || roomSavedUserAuthToken,
    referrer,
  });

  manager.assertIsInitialized();
  manager.setContext({ roomUser: createLoadable(null, true), isRoomUserAuthAttempted: true });

  try {
    if (!roomInjectedAuthToken) {
      /**
       * Validate display name for anonymous users.
       */
      switch (type) {
        case EInteractionAuthOption.ANONYMOUS:
          if (
            typeof roomDisplayName !== 'string' ||
            roomDisplayName.length < interactionToolsConfig.AUTHORIZATION.MIN_DISPLAYNAME_LENGTH ||
            roomDisplayName.length > interactionToolsConfig.AUTHORIZATION.MAX_DISPLAYNAME_LENGTH
          ) {
            throw new LiveError('Invalid auth preferred name supplied.', {
              code: ELiveErrorCode.INVALID_PARAMETERS,
              data: { authType: type },
            });
          }
          break;

        case EInteractionAuthOption.GUEST:
          if (
            typeof roomGuestCode !== 'string' ||
            roomGuestCode.length !== interactionToolsConfig.AUTHORIZATION.ROOM_GUEST_CODE_LENGTH
          ) {
            throw new LiveError('Invalid auth guest code supplied.', {
              code: ELiveErrorCode.INVALID_PARAMETERS,
              data: { authType: type },
            });
          }
          break;

        case EInteractionAuthOption.REGISTRANT:
          // Check optional registrant UUID only if is correct UUID value.
          if (roomRegistrantUuid && roomRegistrantUuid.length !== 36) {
            throw new LiveError(
              'Invalid auth registrant UUID supplied. Expect 36 characters string.',
              {
                code: ELiveErrorCode.INVALID_PARAMETERS,
                data: { authType: type },
              },
            );
          }

          // Check email if registrant UUID not provided.
          if (!roomRegistrantUuid && (typeof roomEmail !== 'string' || !isEmailValid(roomEmail))) {
            throw new LiveError('Invalid auth registrant email supplied.', {
              code: ELiveErrorCode.INVALID_PARAMETERS,
              data: { authType: type },
            });
          }

          break;
      }
    }

    const roomJwtToken: Maybe<string> = roomInjectedAuthToken || roomSavedUserAuthToken;

    const credentials: IInteractionRoomCredentials = await postInteractionRoomAuth(
      {
        gctlConfig: queryManagerGctlConfigWithFallback(manager),
        connection: manager.connections.auth,
        fields: [
          'user',
          'firebase.config',
          'firebase.app_name',
          'firebase.jwt',
          'room_id',
          'room_hash',
          'room_type',
          'metadata.connections.chat_history',
          'metadata.connections.questions_history',
          'metadata.connections.polls_history',
          'metadata.misc',
        ],
      },
      {
        roomDisplayName,
        roomGuestCode,
        authType: type,
        ...(roomUnlistedHash ? { roomUnlistedHash: encodeURIComponent(roomUnlistedHash) } : {}),
        ...(roomPassword ? { roomPassword: encodeURIComponent(roomPassword) } : {}),
        ...(roomHashedPassword
          ? { roomHashedPassword: encodeURIComponent(roomHashedPassword) }
          : {}),
        ...(roomRegistrantUuid ? { roomRegistrantUuid } : {}),
        ...(roomEmail ? { roomEmail } : {}),
        ...(roomJwtToken ? { roomJwtToken } : {}),
        ...(referrer ? { referrer } : {}),
      },
    );

    manager.log.info('Credentials ready:', credentials);
    manager.setContext({
      roomUser: manager.context.roomUser.asReady(credentials.user),
    });

    manager.log.info('Credentials user auth token:', credentials.user.authToken);

    if (credentials.user.authToken) {
      const {
        roomId,
        user: { displayName, authToken },
      } = credentials;

      manager.log.info(
        'Save user auth prefs to localstorage:',
        localStorageConfig.INTERACTION.SAVED_USER_AUTH_PREFERENCES,
        {
          roomId,
          displayName,
          authToken,
        },
      );

      setLocalStorageItem<ISavedUserAuthPreferences>(
        localStorageConfig.INTERACTION.SAVED_USER_AUTH_PREFERENCES,
        {
          roomId,
          displayName,
          authToken,
        },
      );
    }

    manager.emitSignal<TInteractionSessionReadySignalEvent['data']>({
      type: ELiveSignal.INTERACTION_SESSION_READY,
      data: {
        roomId: credentials.roomId,
        roomHash: credentials.roomHash,
        firebase: credentials.firebase,
        user: credentials.user,
        metadata: credentials.metadata,
      },
    });
  } catch (error) {
    manager.log.error('Credentials load failed:', error);

    if (error instanceof LiveError) {
      error.data.authType = type;
    }

    manager.setContext({
      roomUser: manager.context.roomUser.asFailed(error),
    });

    trackLiveError(error, {
      method: 'authorizeInteractionUser',
      category: ELiveErrorCategory.INTERACTION,
    });
  }
}

/**
 * Logout function to clean up all the status info.
 */
export async function logoutInteractionUser(manager: InteractionSessionManager): Promise<void> {
  manager.log.info('Interaction tools user logout');

  const { roomUser } = manager.context;

  if (!roomUser.value) {
    return manager.log.info('Already logged out');
  } else if (roomUser.isLoading) {
    return manager.log.info('Currently auth is in loading status, ignore request');
  }

  manager.setContext({ roomUser: createLoadable(null) });
  manager.emitSignal({ type: ELiveSignal.INTERACTION_SESSION_LOGOUT });
}

/**
 * Reset user auth error.
 */
export function resetAuthError(manager: InteractionSessionManager): void {
  const { roomUser } = manager.context;

  if (roomUser.error) {
    manager.setContext({ roomUser: roomUser.asReady() });
  }
}

/**
 * Interaction room password validation request for check password from user input or query parameter.
 */
export async function checkPasswordAuth(
  manager: InteractionSessionManager,
  password: Maybe<string>,
  type: Maybe<ECredentialsType> = ECredentialsType.PASSWORD,
): Promise<void> {
  const { sessionId, sessionType, roomPassword, roomHashedPassword } = manager.context;

  manager.log.info('Validating room password:', {
    sessionId,
    sessionType,
    password,
  });

  manager.assertIsInitialized();

  try {
    if (
      !password ||
      typeof password !== 'string' ||
      password.length === 0 ||
      (type !== ECredentialsType.PASSWORD && type !== ECredentialsType.HASHED_PASSWORD)
    ) {
      throw new LiveError('Invalid room password supplied.', {
        code: ELiveErrorCode.INVALID_PARAMETERS,
        data: {},
      });
    }

    await postRoomCredentialsCheck(
      {
        gctlConfig: queryManagerGctlConfigWithFallback(manager),
        connection: manager.connections.credentialsCheck,
      },
      {
        type,
        value: encodeURIComponent(password),
      },
    );

    if (type === ECredentialsType.PASSWORD) {
      manager.setContext({ roomPassword: password });
    } else if (type === ECredentialsType.HASHED_PASSWORD) {
      manager.setContext({ roomHashedPassword: password });
    }

    return checkAutoAuth(manager);
  } catch (error) {
    manager.log.error('Password validation failed:', error);

    /**
     * If the room password exists in context from the initial state (query parameter),
     * flush it and set null to the room user to show the password form.
     * Otherwise, set an error to the user to display the password form error.
     */
    if (roomPassword) {
      manager.setContext({
        roomPassword: null,
        roomUser: manager.context.roomUser.asReady(null),
      });
    } else if (roomHashedPassword) {
      manager.setContext({
        roomHashedPassword: null,
        roomUser: manager.context.roomUser.asReady(null),
      });
    } else {
      manager.setContext({
        roomUser: manager.context.roomUser.asFailed(error),
      });
    }

    trackLiveError(error, {
      method: 'checkPasswordAuth',
      category: ELiveErrorCategory.INTERACTION,
    });
  }
}
